/**
 * Shared file utilities for download, formatting, and extension handling.
 *
 * Consolidates duplicated logic previously found across:
 * - DocumentsTab, VendorAssignmentDialog, DocumentPreview, DocumentProcessingMetrics
 * - DownloadMenuItem, jsonExporter
 */

/**
 * Formats a byte count into a human-readable file size string.
 *
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "1.5 KB", "3.2 MB", "0 Bytes")
 */
export const formatFileSize = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined) return "—";
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Triggers a browser file download from a Blob.
 *
 * Creates a temporary object URL and anchor element, triggers the download,
 * then cleans up to prevent memory leaks.
 *
 * @param blob - The file blob to download
 * @param filename - The filename for the downloaded file
 */
export const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Extracts the file extension from a filename, including the leading dot.
 *
 * @param filename - The filename to extract from
 * @returns Lowercase extension with dot (e.g., ".pdf", ".xlsx") or "—" if none
 */
export const getFileExtension = (filename?: string): string => {
  if (!filename) return "—";
  const lastDotIndex = filename.lastIndexOf(".");
  return lastDotIndex !== -1 ? filename.slice(lastDotIndex).toLowerCase() : "—";
};
